import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { forwardRef, type ReactNode } from 'react';
import { cn } from './lib/cn';

export interface ScrollAreaProps {
  children: ReactNode;
  /** `vertical` is the default; message panes use `vertical`, tab rows `horizontal`. */
  orientation?: 'vertical' | 'horizontal' | 'both';
  /** Fade the top/bottom edges to hint at more content. */
  fadeEdges?: boolean;
  /** Only show the scrollbar while the pointer is over the region. */
  quiet?: boolean;
  className?: string;
  viewportClassName?: string;
}

/**
 * Scroll container with the app's thin, neutral scrollbar.
 * The shell never scrolls — every scrollable region uses this.
 */
export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(function ScrollArea(
  { children, orientation = 'vertical', fadeEdges = false, quiet = true, className, viewportClassName },
  ref,
) {
  const showVertical = orientation === 'vertical' || orientation === 'both';
  const showHorizontal = orientation === 'horizontal' || orientation === 'both';

  return (
    <ScrollAreaPrimitive.Root
      type={quiet ? 'hover' : 'always'}
      scrollHideDelay={400}
      className={cn('relative overflow-hidden', className)}
    >
      <ScrollAreaPrimitive.Viewport
        ref={ref}
        className={cn(
          'h-full w-full [&>div]:!block',
          fadeEdges && '[mask-image:linear-gradient(to_bottom,transparent,black_12px,black_calc(100%-12px),transparent)]',
          viewportClassName,
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>

      {showVertical ? (
        <ScrollAreaPrimitive.Scrollbar
          orientation="vertical"
          className="flex w-2 touch-none select-none p-0.5 transition-opacity duration-slow"
        >
          <ScrollAreaPrimitive.Thumb className="flex-1 rounded-pill bg-border-strong/70 hover:bg-muted-foreground/55" />
        </ScrollAreaPrimitive.Scrollbar>
      ) : null}

      {showHorizontal ? (
        <ScrollAreaPrimitive.Scrollbar
          orientation="horizontal"
          className="flex h-2 touch-none select-none flex-col p-0.5 transition-opacity duration-slow"
        >
          <ScrollAreaPrimitive.Thumb className="flex-1 rounded-pill bg-border-strong/70 hover:bg-muted-foreground/55" />
        </ScrollAreaPrimitive.Scrollbar>
      ) : null}

      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
});
